import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Plus, Scales, MagnifyingGlass, Buildings, X, Trash, FilePdf, Robot, Spinner, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import SignatarioPicker from "@/components/SignatarioPicker";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });

const TIPOS = [
  { v: "prazo", l: "Prazo geral (Art. 132)" },
  { v: "prazo_servicos_continuados", l: "Prorrogação — Serviços/Compras continuadas (Art. 107)" },
  { v: "prazo_obras_engenharia", l: "Prorrogação — Obras/Engenharia (Art. 111)" },
  { v: "valor", l: "Valor — acréscimo/supressão (Art. 124-125)" },
  { v: "qualitativo", l: "Qualitativo (Art. 124 §1º)" },
  { v: "misto", l: "Misto (prazo + valor)" },
];

const isPrazoTipo = (t) => t === "prazo" || t === "prazo_servicos_continuados" || t === "prazo_obras_engenharia" || t === "misto";
const isValorTipo = (t) => t === "valor" || t === "misto";

export default function Aditivos() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [motivacaoAditivo, setMotivacaoAditivo] = useState(null);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (tipo) params.tipo = tipo;
    api.get("/aditivos", { params }).then((r) => { setItems(r.data.items); setTotal(r.data.total); });
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search, tipo]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">Lei 14.133/2021</div>
          <h1 className="font-heading text-4xl tracking-tight mt-2">Aditivos Contratuais</h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
            Registro e acompanhamento de termos aditivos celebrados, com base na nova Lei
            de Licitações e Contratos (Art. 124, 125 e 132 da Lei 14.133/2021).
          </p>
        </div>
        <button data-testid="new-aditivo" onClick={() => setShowModal(true)}
                className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium flex items-center gap-2">
          <Plus size={16} weight="bold" /> Registrar aditivo
        </button>
      </div>

      <div className="tce-card p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input data-testid="aditivos-search" value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   placeholder="Buscar por número, contrato ou justificativa…"
                   className="tce-input w-full pl-10 pr-4 py-2.5 rounded-md text-sm" />
          </div>
          <select data-testid="aditivos-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}
                  className="tce-input px-3 py-2.5 rounded-md text-sm">
            <option value="">Todos os tipos</option>
            {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
        <div className="text-xs text-[#94A3B8] mt-3">{total} aditivo(s)</div>
      </div>

      <div className="tce-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#1E293B]">
              <tr className="text-left">
                <th className="px-5 py-4 uppercase-label">Aditivo</th>
                <th className="px-5 py-4 uppercase-label">Contrato</th>
                <th className="px-5 py-4 uppercase-label">Tipo</th>
                <th className="px-5 py-4 uppercase-label">Município</th>
                <th className="px-5 py-4 uppercase-label text-right">Valor +/−</th>
                <th className="px-5 py-4 uppercase-label">Novo prazo</th>
                <th className="px-5 py-4 uppercase-label">Data</th>
                <th className="px-5 py-4 uppercase-label text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-[#94A3B8]">Nenhum aditivo registrado ainda.</td></tr>
              ) : items.map((a, i) => (
                <tr key={a.id} data-testid={`aditivo-row-${i}`} className="border-t border-[#334155] hover:bg-[#1E293B]/60">
                  <td className="px-5 py-4 font-mono text-[#F59E0B]">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{a.numero}</span>
                      {a.pesquisa_precos_id && (
                        <span
                          data-testid={`aditivo-pesquisa-badge-${i}`}
                          title="Pesquisa de preços vinculada (IN 65/2021)"
                          className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-[#10B98115] text-[#10B981] font-sans"
                        >
                          PP
                        </span>
                      )}
                      {a.renovacao_saldo_numero && (
                        <Link to="/renovacoes-saldo" title={`Renovação vinculada: ${a.renovacao_saldo_numero}`}
                              className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-[#009C3B]/20 text-[#12B850] font-sans hover:bg-[#009C3B]/30">
                          {a.renovacao_saldo_numero}
                        </Link>
                      )}
                      {a.notificacao_id && (
                        <Link to="/notificacoes" title="Notificação formal emitida"
                              className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-[#002776]/20 text-[#3B82F6] font-sans hover:bg-[#002776]/30">
                          NOT
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <Link to={`/contratos/${a.contract_id}`} className="font-mono text-[#F59E0B] hover:text-[#FCD34D]">{a.contract_numero}</Link>
                  </td>
                  <td className="px-5 py-4 text-xs">
                    <span className="px-2 py-1 rounded-full bg-[#F59E0B]/15 text-[#F59E0B]">
                      {a.tipo}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-[#94A3B8]">
                    <span className="flex items-center gap-1.5">
                      <Buildings size={12} weight="duotone" className="text-[#F59E0B]" />
                      {a.municipio_nome}
                    </span>
                  </td>
                  <td className={`px-5 py-4 text-right font-mono ${a.valor_adicional > 0 ? "text-[#F59E0B]" : a.valor_adicional < 0 ? "text-[#10B981]" : "text-[#94A3B8]"}`}>
                    {a.valor_adicional ? fmtBRL(a.valor_adicional) : "—"}
                  </td>
                  <td className="px-5 py-4 text-xs text-[#94A3B8]">{a.novo_prazo_fim?.slice(0, 10) || "—"}</td>
                  <td className="px-5 py-4 text-xs text-[#94A3B8]">{new Date(a.created_at).toLocaleDateString("pt-BR")}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        data-testid={`aditivo-editor-${i}`}
                        onClick={() => navigate(`/editor/aditivo/${a.id}`)}
                        title="Abrir Editor Inteligente da motivação (revisão com IA)"
                        className="p-1.5 rounded text-[#94A3B8] hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
                      >
                        <PencilSimple size={14} weight="duotone" />
                      </button>
                      <button
                        data-testid={`aditivo-motivacao-ia-${i}`}
                        onClick={() => setMotivacaoAditivo(a)}
                        title="Gerar/visualizar motivação com IA"
                        className="p-1.5 rounded text-[#94A3B8] hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
                      >
                        <Robot size={14} weight="duotone" />
                      </button>
                      <button
                        data-testid={`aditivo-pdf-${i}`}
                        onClick={() => setMotivacaoAditivo(a)}
                        title="Baixar Relatório de Motivação (PDF)"
                        className="p-1.5 rounded text-[#94A3B8] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors"
                      >
                        <FilePdf size={14} weight="duotone" />
                      </button>
                      <button
                        data-testid={`aditivo-delete-${i}`}
                        onClick={async () => {
                          if (!window.confirm(`Excluir o aditivo "${a.numero}" do contrato ${a.contract_numero}? Esta ação é irreversível.`)) return;
                          try {
                            await api.delete(`/aditivos/${a.id}`);
                            toast.success("Aditivo excluído");
                            load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Erro ao excluir");
                          }
                        }}
                        title="Excluir aditivo"
                        className="p-1.5 rounded text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                      >
                        <Trash size={14} weight="duotone" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && <NovoAditivoModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
      {motivacaoAditivo && <MotivacaoModal aditivo={motivacaoAditivo} onClose={() => setMotivacaoAditivo(null)} />}
    </div>
  );
}

function NovoAditivoModal({ onClose, onSaved }) {
  const [contracts, setContracts] = useState([]);
  const [pesquisas, setPesquisas] = useState([]);
  const [loadingPesq, setLoadingPesq] = useState(false);
  const [form, setForm] = useState({
    contract_id: "", numero: "", tipo: "prazo",
    valor_adicional: "", novo_prazo_fim: "", justificativa: "",
    pesquisa_precos_id: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/contracts?limit=200").then((r) => setContracts(r.data.items));
  }, []);

  // Carrega pesquisas de preços ao escolher o contrato
  useEffect(() => {
    if (!form.contract_id) { setPesquisas([]); return; }
    let cancelled = false;
    setLoadingPesq(true);
    api.get(`/contracts/${form.contract_id}/pesquisas-precos`)
      .then((r) => { if (!cancelled) setPesquisas(r.data?.items || []); })
      .catch(() => { if (!cancelled) setPesquisas([]); })
      .finally(() => { if (!cancelled) setLoadingPesq(false); });
    return () => { cancelled = true; };
  }, [form.contract_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contract_id) { toast.error("Selecione um contrato"); return; }
    setSaving(true);
    try {
      const payload = {
        numero: form.numero, tipo: form.tipo,
        valor_adicional: parseFloat(form.valor_adicional) || 0,
        novo_prazo_fim: form.novo_prazo_fim || null,
        justificativa: form.justificativa,
        pesquisa_precos_id: form.pesquisa_precos_id || null,
      };
      const { data } = await api.post(`/contracts/${form.contract_id}/aditivos`, payload);
      const after = data?.contract_after || {};
      const upd = data?.updates_aplicados || {};
      const fmt = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
      let detalhe = "Aditivo registrado com sucesso";
      if ("valor" in upd) {
        detalhe += ` · Contrato atualizado para ${fmt(after.valor)} (saldo: ${fmt(after.saldo)})`;
      }
      if ("data_fim" in upd) {
        const novaData = after.data_fim ? new Date(after.data_fim).toLocaleDateString("pt-BR") : "—";
        detalhe += ` · Nova vigência até ${novaData}`;
      }
      toast.success(detalhe);
      if (data?.renovacao_criada?.numero_gerado) {
        toast.info(`Renovação de Saldo ${data.renovacao_criada.numero_gerado} gerada automaticamente — abra em "Renovações de Saldo" para finalizar`, { duration: 7000 });
      }
      if (data?.notificacao_criada?.id) {
        toast.info(`Notificação formal emitida ao fornecedor (vinculada ao aditivo)`, { duration: 5000 });
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div className="flex items-center gap-2">
            <Scales size={20} weight="duotone" className="text-[#F59E0B]" />
            <div className="font-heading text-xl">Novo aditivo contratual</div>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#F59E0B]"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="uppercase-label block mb-2">Contrato base</label>
            <select data-testid="form-contract" required value={form.contract_id}
                    onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                    className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
              <option value="">Selecione…</option>
              {contracts.map((c) => (
                <option key={c.id} value={c.id}>{c.numero} — {c.municipio_nome} — {c.objeto?.slice(0, 50)}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="uppercase-label block mb-2">Nº do aditivo</label>
              <input data-testid="form-numero" required value={form.numero}
                     onChange={(e) => setForm({ ...form, numero: e.target.value })}
                     placeholder="Ex: 1º Aditivo / 2024"
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
            </div>
            <div>
              <label className="uppercase-label block mb-2">Tipo (Lei 14.133)</label>
              <select data-testid="form-tipo" value={form.tipo}
                      onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm">
                {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
          </div>
          {isValorTipo(form.tipo) && (
            <div>
              <label className="uppercase-label block mb-2">Acréscimo/Supressão (R$) — Art. 125 limita a ±25%</label>
              <input data-testid="form-valor" type="number" step="0.01" value={form.valor_adicional}
                     onChange={(e) => setForm({ ...form, valor_adicional: e.target.value })}
                     placeholder="Positivo: acréscimo · Negativo: supressão"
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm font-mono" />
            </div>
          )}
          {isPrazoTipo(form.tipo) && (
            <div>
              <label className="uppercase-label block mb-2">
                Novo prazo final de vigência
                {form.tipo === "prazo_servicos_continuados" && " · Art. 107 permite até 10 anos"}
                {form.tipo === "prazo_obras_engenharia" && " · Art. 111 conforme cronograma físico-financeiro"}
              </label>
              <input data-testid="form-prazo" type="date" value={form.novo_prazo_fim}
                     onChange={(e) => setForm({ ...form, novo_prazo_fim: e.target.value })}
                     className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
            </div>
          )}
          <div>
            <label className="uppercase-label block mb-2">
              Pesquisa de preços vinculada
              <span className="ml-2 text-[10px] text-[#94A3B8] normal-case">
                · IN SEGES/ME nº 65/2021 (recomendada para acréscimos de valor)
              </span>
            </label>
            <select
              data-testid="form-pesquisa-precos"
              value={form.pesquisa_precos_id}
              onChange={(e) => setForm({ ...form, pesquisa_precos_id: e.target.value })}
              disabled={!form.contract_id || loadingPesq}
              className="tce-input w-full px-3 py-2.5 rounded-md text-sm disabled:opacity-50"
            >
              <option value="">
                {!form.contract_id ? "Selecione um contrato primeiro" :
                 loadingPesq ? "Carregando…" :
                 pesquisas.length === 0 ? "Nenhuma pesquisa cadastrada para este contrato" :
                 "Sem vínculo / Anexar depois"}
              </option>
              {pesquisas.map((p) => {
                const data = new Date(p.created_at).toLocaleDateString("pt-BR");
                const media = p.estatisticas?.media || 0;
                const n = p.itens?.length || 0;
                return (
                  <option key={p.id} value={p.id}>
                    {data} · {p.termo} · {n} amostras · média {fmtBRL(media)}
                  </option>
                );
              })}
            </select>
            {isValorTipo(form.tipo) && !form.pesquisa_precos_id && (
              <div className="text-[11px] text-[#F59E0B] mt-1.5 flex items-start gap-1.5">
                <Scales size={11} className="mt-0.5 shrink-0" />
                <span>Aditivos de <strong>valor</strong> exigem pesquisa de preços formal conforme IN 65/2021. Crie uma na página do contrato antes de prosseguir.</span>
              </div>
            )}
          </div>
          <div>
            <label className="uppercase-label block mb-2">Justificativa técnica</label>
            <textarea data-testid="form-just" required rows={4} value={form.justificativa}
                      onChange={(e) => setForm({ ...form, justificativa: e.target.value })}
                      placeholder="Fundamentação técnica e legal do aditivo…"
                      className="tce-input w-full px-3 py-2.5 rounded-md text-sm" />
          </div>
          <div className="rounded-md bg-[#F59E0B]/5 border border-[#F59E0B]/30 p-3 text-xs text-[#FCD34D]">
            <strong>Base legal:</strong> Art. 124, 125 e 132 da Lei 14.133/2021 — A celebração de aditivos
            requer justificativa formal e respeito aos limites legais de acréscimo/supressão.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-md border border-[#334155] hover:border-[#94A3B8] text-sm">
              Cancelar
            </button>
            <button data-testid="form-save" disabled={saving}
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm disabled:opacity-50">
              {saving ? "Registrando…" : "Registrar aditivo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function MotivacaoModal({ aditivo, onClose }) {
  const [motivacao, setMotivacao] = useState(aditivo.motivacao_ia || "");
  const [contexto, setContexto] = useState("");
  const [generating, setGenerating] = useState(false);
  const [signatarioId, setSignatarioId] = useState("");

  const gerarIA = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/aditivos/${aditivo.id}/motivacao/ai`, { contexto_extra: contexto || null });
      setMotivacao(data.motivacao);
      toast.success("Motivação gerada pela IA");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar com IA");
    } finally {
      setGenerating(false);
    }
  };

  const baixarPdf = async () => {
    if (!signatarioId) {
      toast.error("Selecione o assinante do documento antes de gerar o PDF");
      return;
    }
    try {
      const res = await fetch(`${API}/aditivos/${aditivo.id}/relatorio-motivacao?signatario_id=${encodeURIComponent(signatarioId)}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `motivacao_aditivo_${aditivo.numero || aditivo.id.slice(0, 8)}.pdf`; link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF baixado");
    } catch {
      toast.error("Erro ao gerar PDF");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div>
            <div className="uppercase-label">Relatório de Motivação</div>
            <div className="font-heading text-xl mt-1">Aditivo {aditivo.numero} — Contrato {aditivo.contract_numero}</div>
            <div className="text-xs text-[#94A3B8] mt-1">{aditivo.tipo_descricao || aditivo.tipo} · {aditivo.municipio_nome}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-[#334155] text-[#94A3B8]" data-testid="motivacao-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="rounded-md bg-[#8B5CF6]/5 border border-[#8B5CF6]/30 p-4">
            <div className="flex items-start gap-3">
              <Robot size={20} weight="duotone" className="text-[#8B5CF6] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#E9EEF5] mb-1">Geração assistida por IA (Claude Sonnet 4.5)</div>
                <div className="text-xs text-[#94A3B8]">
                  A IA elabora relatório fático, fundamentação jurídica (Lei 14.133/2021), justificativa técnica, análise de vantajosidade e conclusão. Utilize os dados já registrados no aditivo e adicione contexto abaixo se necessário. <strong className="text-[#FCD34D]">A geração leva ~1 a 2 minutos.</strong>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="uppercase-label block mb-2">Contexto adicional para a IA (opcional)</label>
            <textarea
              data-testid="motivacao-contexto"
              rows={3}
              value={contexto}
              onChange={(e) => setContexto(e.target.value)}
              placeholder="Ex.: houve atraso na entrega de insumos, ou necessidade de acrescer serviços não previstos originalmente…"
              className="tce-input w-full px-3 py-2.5 rounded-md text-sm"
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="motivacao-gerar-ia"
              onClick={gerarIA}
              disabled={generating}
              className="px-4 py-2.5 rounded-md bg-[#8B5CF6] text-white hover:bg-[#7C3AED] font-medium text-sm disabled:opacity-50 flex items-center gap-2"
            >
              {generating ? <Spinner size={16} className="animate-spin" /> : <Robot size={16} weight="bold" />}
              {generating ? "Gerando com IA…" : (motivacao ? "Regenerar com IA" : "Gerar motivação com IA")}
            </button>
            <button
              data-testid="motivacao-baixar-pdf"
              onClick={baixarPdf}
              disabled={!signatarioId}
              className="px-4 py-2.5 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FilePdf size={16} weight="bold" /> Baixar PDF
            </button>
          </div>

          <div className="rounded-md bg-[#0F172A] border border-[#334155] p-4">
            <SignatarioPicker
              municipioId={aditivo.municipio_id}
              value={signatarioId}
              onChange={setSignatarioId}
              testid="motivacao-signatario"
              required
            />
          </div>

          {motivacao && (
            <div>
              <div className="uppercase-label mb-2">Motivação elaborada</div>
              <div
                data-testid="motivacao-texto"
                className="rounded-md bg-[#0F172A] border border-[#334155] p-5 text-sm text-[#E9EEF5] leading-relaxed whitespace-pre-wrap max-h-[400px] overflow-y-auto font-mono"
              >
                {motivacao}
              </div>
              <div className="text-xs text-[#94A3B8] mt-2">
                Este texto será incluído automaticamente no PDF. Ao regenerar, o texto anterior será substituído.
              </div>
            </div>
          )}

          {!motivacao && aditivo.justificativa && (
            <div>
              <div className="uppercase-label mb-2">Justificativa registrada</div>
              <div className="rounded-md bg-[#0F172A] border border-[#334155] p-4 text-sm text-[#94A3B8] italic">
                "{aditivo.justificativa}"
              </div>
              <div className="text-xs text-[#94A3B8] mt-2">
                Sem motivação IA, o PDF usará este texto. Clique em "Gerar motivação com IA" para produzir texto jurídico completo.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
